import { Container, Text, TextStyle, Graphics, FillGradient } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONTS } from '../constants/theme';
import { UIButton } from './UIButton';
import { SoundManager } from '@services/SoundManager';
import type { PlayerId } from '@/types';

export interface CoinFlipOverlayOptions {
  width: number;
  height: number;
  player1Name: string;
  player2Name: string;
  title?: string;
  onCoinFlip: () => PlayerId;
  onComplete: () => void;
}

/**
 * Reusable coin flip overlay component
 * Shows an animated coin flip and reveals who goes first
 */
export class CoinFlipOverlay extends Container {
  private options: CoinFlipOverlayOptions;
  private coinContainer: Container;
  private coinFrontContainer: Container;
  private coinBackContainer: Container;
  private resultText: Text;
  private continueButton: UIButton;
  private coinFlipResult: PlayerId | null = null;

  constructor(options: CoinFlipOverlayOptions) {
    super();
    this.options = options;

    const { width, height, player1Name, player2Name, title = 'Who Goes First?' } = options;
    const centerX = width / 2;
    const centerY = height / 2;

    // Background overlay
    const bgOverlay = new Graphics();
    bgOverlay.rect(0, 0, width, height);
    bgOverlay.fill({ color: 0x000000, alpha: 0.85 });
    this.addChild(bgOverlay);

    // Title
    const titleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.XXL,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const titleText = new Text({ text: title, style: titleStyle });
    titleText.anchor.set(0.5);
    titleText.x = centerX;
    titleText.y = centerY - 180;
    this.addChild(titleText);

    // Coin container
    this.coinContainer = new Container();
    this.coinContainer.x = centerX;
    this.coinContainer.y = centerY - 40;
    this.addChild(this.coinContainer);

    // Coin front (Player 1)
    this.coinFrontContainer = new Container();
    this.coinContainer.addChild(this.coinFrontContainer);

    const p1Gradient = new FillGradient({
      type: 'linear',
      start: { x: 0.5, y: 0 },
      end: { x: 0.5, y: 1 },
      colorStops: [
        { offset: 0, color: COLORS.PLAYER1 },
        { offset: 1, color: 0x1e40af },
      ],
    });

    const coinFrontGraphics = new Graphics();
    coinFrontGraphics.circle(0, 0, 80);
    coinFrontGraphics.fill(p1Gradient);
    coinFrontGraphics.stroke({ color: 0xffffff, width: 4 });
    this.coinFrontContainer.addChild(coinFrontGraphics);

    const coinFrontText = new Text({
      text: player1Name,
      style: new TextStyle({
        fontFamily: FONTS.PRIMARY,
        fontSize: FONTS.SIZES.LG,
        fontWeight: 'bold',
        fill: COLORS.TEXT_PRIMARY,
      }),
    });
    coinFrontText.anchor.set(0.5);
    // Scale down if name is too wide for the coin
    const maxCoinTextWidth = 120;
    if (coinFrontText.width > maxCoinTextWidth) {
      coinFrontText.scale.set(maxCoinTextWidth / coinFrontText.width);
    }
    this.coinFrontContainer.addChild(coinFrontText);

    // Coin back (Player 2)
    this.coinBackContainer = new Container();
    this.coinBackContainer.visible = false;
    this.coinContainer.addChild(this.coinBackContainer);

    const p2Gradient = new FillGradient({
      type: 'linear',
      start: { x: 0.5, y: 0 },
      end: { x: 0.5, y: 1 },
      colorStops: [
        { offset: 0, color: COLORS.PLAYER2 },
        { offset: 1, color: 0x991b1b },
      ],
    });

    const coinBackGraphics = new Graphics();
    coinBackGraphics.circle(0, 0, 80);
    coinBackGraphics.fill(p2Gradient);
    coinBackGraphics.stroke({ color: 0xffffff, width: 4 });
    this.coinBackContainer.addChild(coinBackGraphics);

    const coinBackText = new Text({
      text: player2Name,
      style: new TextStyle({
        fontFamily: FONTS.PRIMARY,
        fontSize: FONTS.SIZES.LG,
        fontWeight: 'bold',
        fill: COLORS.TEXT_PRIMARY,
      }),
    });
    coinBackText.anchor.set(0.5);
    // Scale down if name is too wide for the coin
    if (coinBackText.width > maxCoinTextWidth) {
      coinBackText.scale.set(maxCoinTextWidth / coinBackText.width);
    }
    this.coinBackContainer.addChild(coinBackText);

    // Result text (hidden initially)
    this.resultText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: FONTS.PRIMARY,
        fontSize: FONTS.SIZES.XL,
        fontWeight: 'bold',
        fill: COLORS.TEXT_PRIMARY,
      }),
    });
    this.resultText.anchor.set(0.5);
    this.resultText.x = centerX;
    this.resultText.y = centerY + 80;
    this.resultText.visible = false;
    this.addChild(this.resultText);

    // Continue button (hidden initially)
    this.continueButton = new UIButton({
      text: 'Start Battle',
      width: 200,
      variant: 'primary',
      onClick: () => this.onContinue(),
    });
    this.continueButton.x = centerX;
    this.continueButton.y = centerY + 150;
    this.continueButton.visible = false;
    this.addChild(this.continueButton);
  }

  /**
   * Start the coin flip animation
   * Call this after adding the overlay to the stage
   */
  start(): void {
    // Fade in
    this.alpha = 0;
    gsap.to(this, {
      alpha: 1,
      duration: 0.3,
      onComplete: () => this.performAnimation(),
    });
  }

  private performAnimation(): void {
    // Play coin flip sound
    SoundManager.play('coinFlip');

    // Get the result from the callback
    this.coinFlipResult = this.options.onCoinFlip();

    const startY = this.coinContainer.y;
    const tossHeight = 250;
    const totalDuration = 1.8;
    const totalFlips = 8 + (this.coinFlipResult === 'player2' ? 1 : 0);

    let showingFront = true;

    // Main timeline
    const timeline = gsap.timeline();

    // Toss up
    timeline.to(this.coinContainer, {
      y: startY - tossHeight,
      duration: totalDuration * 0.4,
      ease: 'power2.out',
    });

    // Fall down
    timeline.to(this.coinContainer, {
      y: startY,
      duration: totalDuration * 0.4,
      ease: 'power2.in',
    });

    // Small bounce
    timeline.to(this.coinContainer, {
      y: startY - 30,
      duration: 0.12,
      ease: 'power2.out',
    });
    timeline.to(this.coinContainer, {
      y: startY,
      duration: 0.12,
      ease: 'power2.in',
    });

    // Tiny bounce
    timeline.to(this.coinContainer, {
      y: startY - 10,
      duration: 0.08,
      ease: 'power2.out',
    });
    timeline.to(this.coinContainer, {
      y: startY,
      duration: 0.08,
      ease: 'power2.in',
    });

    // Spinning animation (runs in parallel with toss)
    const flipTimeline = gsap.timeline();
    const baseFlipDuration = 0.08;

    for (let i = 0; i < totalFlips; i++) {
      const progress = i / totalFlips;
      const flipDuration = baseFlipDuration + progress * 0.12;

      // Squash to flat
      flipTimeline.to(this.coinContainer.scale, {
        x: 0.1,
        y: 1.1,
        duration: flipDuration * 0.5,
        ease: 'power1.in',
        onComplete: () => {
          showingFront = !showingFront;
          this.coinFrontContainer.visible = showingFront;
          this.coinBackContainer.visible = !showingFront;
        },
      });

      // Expand back
      flipTimeline.to(this.coinContainer.scale, {
        x: 1,
        y: 1,
        duration: flipDuration * 0.5,
        ease: 'power1.out',
      });
    }

    // Start flip animation at the same time as toss
    timeline.add(flipTimeline, 0);

    // Add slight wobble during flight
    timeline.to(
      this.coinContainer,
      {
        rotation: 0.1,
        duration: totalDuration * 0.2,
        yoyo: true,
        repeat: 3,
        ease: 'sine.inOut',
      },
      0
    );

    // Reset rotation at end
    timeline.to(this.coinContainer, {
      rotation: 0,
      duration: 0.1,
    });

    // Show result after animation
    timeline.call(() => {
      SoundManager.play('coinLand');

      // Make sure the correct side is showing
      this.coinFrontContainer.visible = this.coinFlipResult === 'player1';
      this.coinBackContainer.visible = this.coinFlipResult === 'player2';

      // Show result text
      const winnerName =
        this.coinFlipResult === 'player1'
          ? this.options.player1Name
          : this.options.player2Name;

      this.resultText.text = `${winnerName} goes first!`;
      this.resultText.visible = true;
      this.resultText.alpha = 0;
      // Scale down if result text is too wide
      const maxResultWidth = this.options.width * 0.8;
      if (this.resultText.width > maxResultWidth) {
        this.resultText.scale.set(maxResultWidth / this.resultText.width);
      }

      gsap.to(this.resultText, { alpha: 1, duration: 0.3 });

      // Show continue button
      this.continueButton.visible = true;
      this.continueButton.alpha = 0;
      gsap.to(this.continueButton, { alpha: 1, duration: 0.3, delay: 0.2 });
    });
  }

  private onContinue(): void {
    // Fade out and call complete callback
    gsap.to(this, {
      alpha: 0,
      duration: 0.3,
      onComplete: () => {
        this.options.onComplete();
      },
    });
  }

  /**
   * Get the result of the coin flip
   */
  getResult(): PlayerId | null {
    return this.coinFlipResult;
  }
}
